import { AvailabilityState } from "../utils/availability";

export type CelularSpecs = {
  procesador: string | null;
  sistema_operativo: string | null;
  ram: string | null;
  almacenamiento: string | null;
  memoria_expandible: string | null;
  tipo_pantalla: string | null;
  tamano_pantalla: string | null;
  propiedades_camara: string | null;
  bateria: string | null;
  factor_forma: string | null;
  ano_lanzamiento: number | null;
  precio_lanzamiento_usd: string | null;
  ranking_2025: number | null;
  ranking_historico: number | null;
  unidades_vendidas_estimadas: string | null;
};

export type ProductRelation = {
  id: string;
  tipo_relacion: "repuesto" | "accesorio" | "compatible_con" | "variante" | "sucesor";
};

export type TipoProducto =
  | "celular"
  | "vidrio"
  | "display"
  | "bateria"
  | "visor"
  | "flex"
  | "protector_camara"
  | "tapa_trasera"
  | "auricular"
  | "altavoz"
  | "camara"
  | "tablet"
  | "otros";

export type ItemType = {
  id?: string;
  name: string;
  description: string;
  available_quantity: number;
  discount: number;
  is_active: boolean;
  price: number;
  picture: string;
  images: string[];

  // How many business days it takes to source this product when it is not in stock — the X in
  // "llega en X días hábiles" (docs/ordering-flow/README.md §3.2). Absent means "use the configured
  // default", which is why it is optional rather than defaulted to a number here: an unset field
  // follows the business's current default, a stored one freezes whatever it was the day someone
  // saved the form. On `SYNC_NEVER_WRITES` — Alegra knows nothing about it.
  lead_time_days?: number | null;

  tipo_producto?: TipoProducto;
  marca?: string;
  modelo?: string;
  variante?: string;
  condicion?: string;
  garantia_meses?: number | null;
  precio_referencia_cop?: number | null;
  url_oficial?: string | null;
  specs?: CelularSpecs | Record<string, unknown>;
  productos_relacionados?: ProductRelation[];
  rating?: number;
  fuente?: string[];
  created_at?: string;
  updated_at?: string;
};

// The slim `GET /items?view=card` projection (EB-495). Carries only what the storefront card
// renders plus the fields the category facets, the favourites filter and the search index need —
// roughly 385 B/row instead of the ~2.0 KB full row. `search_text` replaces `description` in the
// Fuse index; deep matches inside the SEO copy are the one accepted loss.
// See docs/catalog-modernization/README.md §6.1–6.3.
export type ItemCardType = {
  id: string;
  name: string;
  price: number;
  discount: number;
  picture: string;
  is_active: boolean;
  available_quantity: number;
  search_text: string;

  // Absent on rows that don't have them; `slug` starts appearing once its backfill runs (§4.2.2).
  slug?: string;
  rating?: number;
  tipo_producto?: TipoProducto;
};

// The slim `GET /items?view=admin` projection (EB-521). The twelve fields the Item Maintenance grid
// binds columns to, plus nothing else — `RelatedProductsEditor`, the only other consumer, reads just
// `id` and `name`.
//
// Deliberately not `Partial<ItemType>`: the point is that `description`, `picture`, `images` and
// `specs` are *absent by contract*, and a Partial would let a component reach for them and get
// `undefined` at runtime with the compiler's blessing. Editing a full product still goes through
// `getItemById`, which returns the whole row.
export type ItemAdminType = {
  id?: string;
  name: string;
  price: number;
  available_quantity: number;
  discount: number;
  is_active: boolean;

  tipo_producto?: TipoProducto;
  rating?: number;
  marca?: string;
  modelo?: string;
  condicion?: string;

  // A count, not the relations themselves: the grid only ever rendered `.length`, and the raw
  // arrays were 32% of the response. Absent when a product has none. `RelatedProductsEditor` gets
  // the real relations as a prop, and `getItemById` still returns them in full.
  productos_relacionados_count?: number;

  // Where the row came from — `["alegra"]` for anything a sync created or linked. Read-only in the
  // admin (EB-524): the sync owns it, and it is *not* a substitute for the curation filter, which
  // keys on the `alg-` id prefix. 2,011 prod rows carry `["alegra"]` but only 1,837 were
  // sync-created; the other 174 are curated products a run merely linked by `referenceId`.
  fuente?: string[];

  // Joined client-side from `GET /favorite/counts` (EB-523), not stored on the item — a favourite
  // is a user-to-item relationship. Always present on a grid row, 0 when nobody favourited it or
  // when the counts could not be fetched.
  favorites_count?: number;

  // Backs the "sincronizados recientemente" filter (EB-509), not any column. Genuinely optional:
  // three prod rows predate the field, so anything reading it must tolerate its absence.
  updated_at?: string;
};

export type BannerType = {
  id: string;
  title: string;
  image_url: string;
  link_url: string;
  alt_text: string;
  display_order: number;
  is_active: boolean;
};

export type CartItem = ItemType & {
  quantity: number;
  lineTotal?: number;
};

// Verified against the live Alegra /items API (docs/django-retirement-migration/migration-plan.md
// §9) — only the fields the grid renders are typed, plus an index signature since Alegra returns
// many more (category, accounting, tax, customFields, etc.) that this page doesn't need yet.
export type AlegraItemPrice = {
  price?: number;
  currency?: { code?: string; symbol?: string };
};

export type AlegraItemType = {
  id: string;
  name?: string;
  reference?: string | null;
  description?: string | null;
  status?: string;
  price?: AlegraItemPrice[];
  inventory?: {
    unit?: string;
    unitCost?: number;
  };
  [key: string]: unknown;
};

export type AlegraItemsPageType = {
  items: AlegraItemType[];
  start: number;
  limit: number;
  total: number;
};

// ── Alegra catalog sync (EB-496 backend, EB-498 UI) ──────────────────
// Mirrors what `lambda/alegra-sync` persists on a job row. The run happens in a second,
// asynchronous invocation, so the job row is the only window the admin has into it.

export const SYNC_TERMINAL_STATUSES = ["completed", "failed", "rejected"] as const;

export type SyncJobStatus =
  | "queued"
  | "scanning"
  | "fetching"
  | "writing"
  | (typeof SYNC_TERMINAL_STATUSES)[number];

export type SyncCounters = {
  scanned: number;
  fetched: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
};

export type SyncSkipBreakdown = {
  inactive: number;
  service: number;
  no_price: number;
  no_id: number;
  unchanged: number;
};

export type SyncVanishedRow = { id: string; referenceId: string };

// What the run changed commercially rather than how many rows it touched (EB-549). `count` is
// exact; `ids` is capped — this is a report, not the never-truncated rollback list the activation
// backfill keeps. Only storefront-visible products appear: an inactive row resolves to
// `UNAVAILABLE` on both sides of the diff and produces no transition at all.
export type SyncAvailabilityTransition = {
  from: AvailabilityState;
  to: AvailabilityState;
  count: number;
  ids: string[];
};

export type SyncSummary = {
  dry_run: boolean;
  max_items: number | null;
  pages_fetched: number;
  planned: { creates: number; updates: number };
  skipped: SyncSkipBreakdown;
  created_ids: string[];
  updated_ids: string[];
  conflicted_ids: string[];
  conflicted_count: number;
  // Absent on job rows written before EB-549, so every reader has to tolerate `undefined`.
  availability_transitions?: SyncAvailabilityTransition[];
  // Null on a capped run: it reads a prefix of Alegra, so everything past the cap would look
  // like it had vanished.
  vanished_from_alegra: SyncVanishedRow[] | null;
  vanished_count: number | null;
  errors: { id: string; error: string }[];
};

export type SyncJobType = {
  jobId: string;
  status: SyncJobStatus;
  requested_by: string | null;
  dry_run: boolean;
  max_items: number | null;
  counters: SyncCounters;
  summary: SyncSummary | null;
  error: string | null;
  created_at: string;
  updated_at: string;
};

export type StartSyncResponseType = {
  jobId: string;
  status: SyncJobStatus;
  dry_run: boolean;
  max_items: number | null;
  created_at: string;
};

export type Resume = {
  cash_in: number;
  bank_in: number;
  cash_out: number;
  bank_out: number;
  cash_balance: number;
  bank_balance: number;
};

export type Payment = {
  /**
   * A number from Alegra, a **uuid string** from `GET /v1/reports/daily-resume` (REP-1, decision of
   * 2026-08-13). Widened rather than converted: nothing does arithmetic on it — it is a React key
   * and a lookup — and minting a surrogate integer would invent an identifier that means nothing on
   * either side.
   */
  id: string | number;
  date: string;
  number: string;
  amount: number;
  type: "in" | "out";
  paymentMethod: "cash" | "bank";
  /**
   * **`void` was missing**, and `lambda/budget/utils/resume.mjs` has always filtered on exactly that
   * value — `if (payment.status === "void") continue`. So the enum said a voided payment could not
   * occur while the code that computes every total depended on it occurring. Found by REP-1, when
   * the compiler rejected the same comparison in a new screen.
   */
  status: "open" | "closed" | "void";
  observations: string | null;
  client: {
    name: string;
    phone: string | null;
    identification: string | null;
  };
  bankAccount: {
    name: string;
  };
  invoices: Array<{
    id: number;
    number: string;
    amount: number;
  }>;
  categories: Array<{
    name: string;
  }>;
};

export type DailyBudgetResponse = {
  resume: Resume;
  payments: Payment[];
};

export type DailyBudget = {
  date: string;
  cash_initial: number;
  bank_initial: number;
  cash_final: number;
  bank_final: number;
  cash_in: number;
  bank_in: number;
  cash_out: number;
  bank_out: number;
};

export type UpdateDailyBudgetRequest = Pick<DailyBudget, "date" | "cash_initial" | "bank_initial">;

export type OrderItem = {
  item_id?: string;
  name: string;
  price: number;
  quantity: number;
  picture?: string;
  discount?: number;
  discounted_price?: number;
  /**
   * What this line was, on the day (EB-548). Snapshotted onto the order rather than looked up
   * later: Sunday's Alegra run rewrites `price` and `available_quantity` every week, and what the
   * shopper was promised must not change with the stock.
   */
  availability_state?: string;
  /** `0` means "in stock today" and `null` means "no knowable date". Both are real answers. */
  lead_time_days?: number | null;
};

export type Order = {
  id: string;
  name: string;
  phone: string;
  items: OrderItem[];
  status: string;
  order_total: number;
  order_time: string;
  email: string;
  address: string;
  payment_method: string;
  delivery_method: string;
  delivery_date: string;
  delivery_status: string;
  user_id: string;
  user_has_been_contacted: boolean;
  updated_at: string;

  // ── EB-548: what was promised, as the server computed it ───────────
  delivery_municipality_id?: string;
  delivery_municipality?: string;
  delivery_instructions?: string;
  pickup_address?: string;
  items_subtotal?: number;
  delivery_fee?: number;
  free_shipping_applied?: boolean;
  has_quote_items?: boolean;
  quote_item_count?: number;
  promise_days_min?: number | null;
  promise_days_max?: number | null;
  /** `order_time + price_hold_days`, so the advisor and the shopper read the same date. */
  price_hold_until?: string | null;
  /**
   * The server's totals differed from the browser's and the server's won. Not an error — the
   * normal cause is the shop editing a fee while somebody had checkout open (README §6.2).
   */
  totals_adjusted?: boolean;
  customer_notes?: string;
  internal_notes?: string;
};

/**
 * What `POST /orders` answers with: the id, plus the figures the server actually stored.
 *
 * The confirmation page renders these rather than what the browser posted, because those are not
 * always the same numbers and the shop honours only one of them.
 */
export type CreateOrderResponse = {
  message?: string;
  orderId: string;
  items_subtotal: number;
  delivery_fee: number;
  order_total: number;
  free_shipping_applied: boolean;
  has_quote_items: boolean;
  quote_item_count: number;
  promise_days_min: number | null;
  promise_days_max: number | null;
  price_hold_until: string | null;
  totals_adjusted: boolean;
  delivery_method: string;
  delivery_municipality_id: string;
  delivery_municipality: string;
  pickup_address: string;
};

export type EducationItem = {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "inactive" | "draft";
  category: "program" | "course";
  start_date: string | null;
  end_date: string | null;
  duration: string | null;
  max_capacity: number;
  picture: string | null;
  images: string[];
};

// Phase 3 (EB-482): enrollments live in DynamoDB keyed by (sub, education_item_id). The Django
// surrogate `id`/`user` fields are gone; identity is the Auth0 `sub`, resolved server-side.
export type Enrollment = {
  sub: string;
  education_item_id: string;
  status: "enrolled" | "completed" | "dropped";
  role: "student" | "instructor";
  enrolled_at: string;
  completed_at: string | null;
};

// Phase 3 (EB-482): favorites live in DynamoDB keyed by (sub, item_id). No surrogate row id — the
// composite key is the identity, so removal is by item_id.
export type UserFavorite = {
  sub: string;
  item_id: string;
  created_at: string;
};
